import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import TrinityMotif from "@/components/TrinityMotif";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, apiError, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Welcome to Trinity.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <p className="overline text-[#7C3AED] mb-2">Create account</p>
          <h1 className="font-heading font-bold text-3xl tracking-tight mb-8">Deploy your agents</h1>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-secondary">Full name</Label>
              <Input value={form.name} onChange={set("name")} required data-testid="register-name-input"
                className="rounded-sm bg-card border-border mt-1.5 h-11" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs text-secondary">Company</Label>
              <Input value={form.company} onChange={set("company")} data-testid="register-company-input"
                className="rounded-sm bg-card border-border mt-1.5 h-11" placeholder="Acme Corp" />
            </div>
            <div>
              <Label className="text-xs text-secondary">Work email</Label>
              <Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input"
                className="rounded-sm bg-card border-border mt-1.5 h-11" placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-xs text-secondary">Password</Label>
              <Input type="password" value={form.password} onChange={set("password")} required minLength={10} placeholder="10+ chars, upper, lower, number, symbol" data-testid="register-password-input"
                className="rounded-sm bg-card border-border mt-1.5 h-11" />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="register-submit-btn"
            className="rounded-sm w-full mt-8 h-11 bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
          <p className="text-sm text-secondary mt-6 text-center">
            Already registered? <Link to="/login" className="text-[#3A2DB8] hover:underline font-semibold" data-testid="to-login-link">Sign in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:flex relative border-l border-border grid-lines order-1 lg:order-2 flex-col justify-between p-12">
        <TrinityMotif className="w-full max-w-md mx-auto mt-6 drop-shadow-[0_30px_60px_rgba(58,45,184,0.25)]" />
        <div>
          <p className="overline text-[#7C3AED] mb-3">Specia AI TestAgents as a Service</p>
          <h2 className="neon-word text-4xl leading-none">TRINITY</h2>
          <p className="font-heading font-semibold text-[#1E1560] mt-4">Every release, tested.</p>
          <p className="text-secondary mt-2 max-w-sm">Autonomous AI agents scan, build and run your SAP regression suites.</p>
        </div>
      </div>
    </div>
  );
}
